// Copyright ©️ 2026 eVolve MEP, LLC

import type { BendTypeInput } from '@evolvemep/api-ts';
import { useMantineTheme } from '@mantine/core';

import { DropdownNumberInput } from 'components/Mantine/DropdownNumberInput';
import { InchesInput } from 'components/Mantine/InchesInput';
import { toHighlightableObjectName } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/HighlightableGroup';
import { useBendPreviewContext } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/useBendPreviewContext';
import { useBendCreatorFormContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorFormContext';
import { useBendCreatorValidationContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorValidationContext';

const DEFAULT_DEGREE_VALUES = [5, 10, 12.25, 22.5, 30, 45, 60, 90];

type Props = {
  bendTypeInput: BendTypeInput;
  isStockLengthField?: boolean;
};

export const BendDimensionInput = ({
  bendTypeInput: { name, keyName, isRequired, isAngle },
  isStockLengthField,
}: Props) => {
  const {
    values: { maximizeLegLength: isUsingStockLength },
  } = useBendCreatorFormContext();
  const { highlightedObject, setHighlightedObject } = useBendPreviewContext();
  const labelObjectName = toHighlightableObjectName(name);
  const theme = useMantineTheme();

  const { wrappedGetInputProps } = useBendCreatorValidationContext();

  return isAngle ? (
    <DropdownNumberInput
      suffix="&deg;"
      options={DEFAULT_DEGREE_VALUES}
      label={name}
      required={isRequired}
      decimalScale={2}
      min={0.01}
      max={90}
      placeholder="Enter degrees"
      hideControls
      c={highlightedObject === labelObjectName ? theme.primaryColor : undefined}
      onMouseEnter={() => setHighlightedObject(labelObjectName)}
      onMouseLeave={() => setHighlightedObject(null)}
      {...wrappedGetInputProps(keyName)}
    />
  ) : (
    <InchesInput
      label={name}
      required={isRequired}
      allowBlank={!isRequired}
      valueInFeet={true}
      c={highlightedObject === labelObjectName ? theme.primaryColor : undefined}
      onMouseEnter={() => setHighlightedObject(labelObjectName)}
      onMouseLeave={() => setHighlightedObject(null)}
      {...(isStockLengthField && isUsingStockLength
        ? {
            disabled: true,
            readOnly: true,
            description: 'This field will use the remaining stock length.',
          }
        : {})}
      {...wrappedGetInputProps(keyName)}
    />
  );
};
